/* Si las dos altas de un chofer se cruzaron, o cuál falta.
 *
 * ── EL PROBLEMA, EN LAS PALABRAS DE IGNA ─────────────────────────────────
 *
 * *«RRHH da de alta a X chofer pero no saben si tiene el registro al día;
 * Marcos no lo da de alta pero mantiene la documentación, pero no sabe dónde
 * está»*. Son dos altas por caminos distintos y nadie las concilia. Medido el
 * 27/08/2026:
 *
 *     160  cruzan bien
 *      10  en la nómina de Erick, sin ficha con Marcos
 *      60  activos con Marcos, sin estar en la nómina
 *
 * ── UN HUECO NO ES UN ERROR, Y NO SE DIBUJA COMO UNO ────────────────────
 *
 * Los 60 no son un problema por sí solos: un fletero legítimo se ve
 * exactamente igual que una baja que no llegó a documentación. Lo que sí es un
 * problema es que hasta hoy **no se veían**, y quien abría la ficha leía media
 * pantalla vacía — que es indistinguible de un dato que falta cargar, y manda
 * a completar algo que en realidad hay que conciliar.
 *
 * Por eso el texto dice qué significa el hueco Y cuántos casos como ése hay:
 * un aviso que parece único se investiga, y uno que se sabe compartido por 60
 * se entiende como lo que es —dos listas que todavía no se cruzaron—.
 *
 * ── Y NO SE ARREGLA DESDE ACÁ ──────────────────────────────────────────
 *
 * La ficha es de lectura. Cuál de los dos padrones tiene razón lo decide una
 * persona, y darlo de alta del otro lado sería escribir en la tabla de otro
 * sector desde una pantalla que no es la suya.
 *
 * ── «NO TIENE FICHA CON MARCOS» ES SÓLO PARA CHOFERES ────────────────────
 *
 * La planilla de documentación es de los que manejan: un administrativo en
 * la nómina no tiene ficha con Marcos porque no tiene registro que vencer, no
 * porque falte cargarlo. El aviso salía igual para cualquiera de la nómina y
 * le contaba a una persona de oficina que «no sabemos si tiene el registro al
 * día» — y los «10 así» del texto son choferes. Ahora mira `es_chofer`.
 */
export const avisaSinFicha = (cruce) =>
  Boolean(cruce?.falta?.includes("documentos") && cruce?.enNomina?.es_chofer === true);
export const avisaSinNomina = (cruce) => Boolean(cruce?.falta?.includes("nomina"));

const DosAltas = ({ cruce }) => {
  const { enNomina, enDocumentos, falta } = cruce;

  return (
    <>
      <ul className="pf-lista">
        <li>
          <b>RRHH</b> ·{" "}
          {enNomina ? (
            <>
              {enNomina.calificacion || enNomina.categoria || "en la nómina"}
              {enNomina.legajo ? ` · legajo ${enNomina.legajo}` : ""}
              {enNomina.trabaja ? "" : " · no figura trabajando"}
            </>
          ) : (
            <span className="tenue">no está en la nómina</span>
          )}
        </li>
        <li>
          <b>Documentación</b> ·{" "}
          {enDocumentos ? (
            <>
              {enDocumentos.nombre}
              {enDocumentos.activo ? "" : " · de baja"}
            </>
          ) : (
            <span className="tenue">no tiene ficha</span>
          )}
        </li>
      </ul>

      {avisaSinNomina(cruce) && (
        <p className="pf-nopude">
          <b>Tiene ficha de documentación y no está en la nómina.</b> Puede ser
          un fletero, alguien de un tercero, o una baja que no llegó a
          documentación. <b>Son 60 así</b> — no es un caso aislado, y hasta que
          alguien decida cuál de los dos padrones manda, la mitad de esta ficha
          no existe.
        </p>
      )}
      {avisaSinFicha(cruce) && (
        <p className="pf-nopude">
          <b>Está en la nómina y no tiene ficha de documentación.</b> Nadie cargó sus
          vencimientos, así que <b>no sabemos si tiene el registro al día</b>.
          Son 10 así.
        </p>
      )}
      {falta.length === 0 && (
        <p className="tenue pf-chico">
          Las dos altas se cruzaron. Es el caso de 160 de los 230.
        </p>
      )}
    </>
  );
};

export default DosAltas;
